import { imzMarginColor, nfzColor, PolygonWithOutlineColor, zoneColor } from '@utils/map/map.constants';
import { computeZoneMargin, getNoFlyZoneCriticality, getProjectionOf3dSolid } from '@utils/map/zone.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Feature, Polygon } from 'geojson';

import {
  InitializationMaskingZone,
  NoFlyZone,
  NoFlyZone3d,
  NoInitZone,
  Zone,
  ZoneTypeEnum,
} from '@/types/config/config.types';
import { FeatureTypeEnum } from '@/types/map.types';

function toZoneFeature(zone: Zone, type: FeatureTypeEnum, color: PolygonWithOutlineColor): Feature {
  let polygon: Polygon;
  if ('solidPolygon' in zone) {
    polygon = getProjectionOf3dSolid(zone.solidPolygon);
  } else {
    polygon =
      'type' in zone.polygon
        ? zone.polygon
        : { type: 'Polygon', coordinates: [zone.polygon.map((coordinates) => toLonLatArray(coordinates, false)!)] };
  }
  return {
    type: 'Feature',
    properties: {
      id: zone.id,
      featureId: `${type}-${zone.id}`,
      featureType: type,
      active: zone.active,
      value: zone,
      // Prevent a npe for replay older than 5.7.0.0
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      fillColor: color?.fill,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      outlineColor: color?.outline,
    },
    geometry: polygon,
  };
}

export const toNIZFeatures = (nizs: NoInitZone[]): Feature[] =>
  nizs.map((niz) => toZoneFeature(niz, FeatureTypeEnum.NO_INIT_ZONE, zoneColor[ZoneTypeEnum.NIZ]!));

export const toNFZFeatures = (nfzs: NoFlyZone[]): Feature[] =>
  nfzs
    .toSorted((nfz1, nfz2) => getNoFlyZoneCriticality(nfz1) - getNoFlyZoneCriticality(nfz2))
    .map((nfz) => toZoneFeature(nfz, FeatureTypeEnum.NO_FLY_ZONE, nfzColor[nfz.level]));

export const toNFZ3dFeatures = (nfzs: NoFlyZone3d[]): Feature[] =>
  nfzs
    .toSorted((nfz1, nfz2) => getNoFlyZoneCriticality(nfz1) - getNoFlyZoneCriticality(nfz2))
    .map((nfz) => toZoneFeature(nfz, FeatureTypeEnum.NO_FLY_ZONE_3D, nfzColor[nfz.level]));

export const toIMZFeatures = (imzs: InitializationMaskingZone[]): Feature[] =>
  imzs.map((imz) => toZoneFeature(imz, FeatureTypeEnum.INITIALIZATION_MASKING_ZONE, zoneColor[ZoneTypeEnum.IMZ]!));

export const toIMZMarginFeatures = (imzs: InitializationMaskingZone[]): Feature[] =>
  imzs.map((imz) => {
    return {
      type: 'Feature',
      properties: {
        id: imz.id,
        featureId: `${FeatureTypeEnum.INITIALIZATION_MASKING_ZONE_MARGIN}-${imz.id}`,
        featureType: FeatureTypeEnum.INITIALIZATION_MASKING_ZONE_MARGIN,
        active: imz.active,
        value: imz,
        fillColor: imzMarginColor.fill,
        outlineColor: imzMarginColor.outline,
      },
      geometry: {
        type: 'Polygon',
        coordinates: computeZoneMargin(imz),
      },
    };
  });
